/**
 * Helpers for gradient-filled text (CSS `background-clip: text`).
 *
 * When a text node has a non-empty `backgroundImage`, the renderer clips that
 * paint to the glyphs and makes the solid text fill transparent so the
 * gradient shows through (metallic / chrome titles, etc.).
 */

/**
 * Returns true when `backgroundImage` should drive a glyph-clipped gradient fill.
 *
 * @param backgroundImage - Raw style value (string gradient or empty/undefined).
 * @returns Whether the value is a non-empty string after trim.
 */
export function hasTextGradientFill(backgroundImage: unknown): boolean {
  return (
    typeof backgroundImage === "string" && backgroundImage.trim().length > 0
  );
}

/**
 * CSS properties that paint a gradient clipped to text glyphs.
 *
 * Apply on the element that owns the text content (not a parent wrapper alone).
 *
 * @param backgroundImage - Non-empty CSS `background-image` value (typically a gradient).
 * @returns Camel-cased CSS props suitable for React `style` / MUI `sx`.
 */
export function getTextGradientFillCss(
  backgroundImage: string,
): Record<string, string> {
  return {
    backgroundImage: backgroundImage.trim(),
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
    color: "transparent",
  };
}
